import * as React from "react";
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import DialogTitle from "@mui/joy/DialogTitle";
import DialogContent from "@mui/joy/DialogContent";
import {
  Button,
  FormControl,
  FormLabel,
  Input,
  ModalClose,
  Stack,
} from "@mui/joy";
import { useNavigate, useParams } from "react-router-dom";
import Typography from "@mui/joy/Typography";
import { useTranslation } from "../../../i18n";
import {
  useTeamInfoQuery,
  useTeamInviteMemberMutation,
} from "../../../__generated__/types-and-hooks";
import { useNotify } from "../../../hooks/useNotify";

export interface InviteMemberModalProps {
  open: boolean;
}

export default function InviteMemberModal({ open }: InviteMemberModalProps) {
  const notify = useNotify();

  const [teamInviteMember] = useTeamInviteMemberMutation({});

  const [email, setEmail] = React.useState("");

  const handleChangeEmail = React.useCallback(
    (e: React.FocusEvent<HTMLInputElement, Element>) => {
      setEmail(e.target.value);
    },
    [setEmail]
  );

  const navigate = useNavigate();
  const t = useTranslation();
  const { teamSlug } = useParams();
  const teamInfoQuery = useTeamInfoQuery({ variables: { slug: teamSlug! } });
  const team = teamInfoQuery.data?.team_by_slug;

  const [saving, setSaving] = React.useState(false);

  const handleInvite = React.useCallback(async () => {
    setSaving(true);
    try {
      await teamInviteMember({
        variables: {
          teamId: team?.id!,
          email,
        },
        onCompleted() {
          notify({
            type: "success",
            msg: "A new member has been invited successfully.",
          });
          navigate("..");
        },
      });
    } finally {
      setSaving(false);
    }
  }, [team, email, teamInviteMember, notify, navigate]);

  return (
    <Modal open={open}>
      <ModalDialog>
        <ModalClose
          onClick={() =>
            navigate("..", {
              relative: "path",
            })
          }
        />
        <DialogTitle>{t("Invite Members")}</DialogTitle>
        <DialogContent>
          <Stack gap={4} sx={{ mt: 2 }}>
            <Typography>
              {t("You're inviting members to {team}", {
                team: team?.name ?? team?.slug ?? "",
              })}
            </Typography>
            <FormControl required>
              <FormLabel>{t("Please enter a user email")}</FormLabel>
              <Input
                size="md"
                onChange={handleChangeEmail}
                placeholder="email"
              />
            </FormControl>
            <Stack gap={4} sx={{ mt: 2 }}>
              <Button
                disabled={saving}
                loading={saving}
                type="submit"
                fullWidth
                onClick={handleInvite}
              >
                {t("Send Invitation Email")}
              </Button>
            </Stack>
          </Stack>
        </DialogContent>
      </ModalDialog>
    </Modal>
  );
}
